import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { useActivity } from './ActivityProvider';

/**
 * ActivityPill — global topbar status indicator + expanded panel + done toast.
 * Reads from ActivityContext. Renders nothing when activity == null.
 */
function ActivityToast({ data, onView }) {
  return (
    <div className="act-toast" role="status">
      <span className="act-toast-ico">
        <I.check size={14} />
      </span>
      <span className="act-toast-msg">{data.message || 'Done'}</span>
      {onView && (
        <button className="act-toast-link" type="button" onClick={onView}>
          View
        </button>
      )}
    </div>
  );
}

export default function ActivityPill() {
  const { activity, setActivity } = useActivity();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const ref = useRef(null);
  const prevKind = useRef(null);

  const openBatch = (batchId) => {
    if (!batchId) return;
    setOpen(false);
    navigate(`/app/batch/${batchId}`);
  };

  // Show toast on active→done transition
  useEffect(() => {
    if (!activity) return;
    if (prevKind.current === 'active' && activity.kind === 'done') {
      setToast(activity);
      const t = setTimeout(() => {
        setToast(null);
        setActivity(null);
      }, 4200);
      return () => clearTimeout(t);
    }
    prevKind.current = activity.kind;
  }, [activity, setActivity]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!activity) {
    return toast ? (
      <ActivityToast
        data={toast}
        onView={toast.batchId ? () => openBatch(toast.batchId) : null}
      />
    ) : null;
  }
  if (activity.kind === 'done') {
    return (
      <ActivityToast
        data={activity}
        onView={activity.batchId ? () => openBatch(activity.batchId) : null}
      />
    );
  }

  const pct =
    activity.kind === 'active' && activity.total > 0
      ? Math.round((activity.current / activity.total) * 100)
      : null;

  // Pill body opens the batch detail screen directly — most users want
  // "show me what's happening" in one click. The chevron next to the body
  // still toggles the inline drop-down for the rare power-user who wants
  // the full activity panel without leaving the current page.
  // `returnTo` is set by callers like EditPost that want the pill to link
  // back to a SPECIFIC page (not a batch) — image regen needs to land
  // back on /app/post/:id/edit?tab=media. Takes priority over batchId.
  const pillBodyAction = activity.returnTo
    ? () => { setOpen(false); navigate(activity.returnTo); }
    : activity.batchId
    ? () => openBatch(activity.batchId)
    : () => setOpen((o) => !o);

  // Does the activity have numerical progress to show? Batch generations
  // do (current/total/%); ad-hoc tasks like image regen, manual edits, or
  // strategy saves don't — they're step-only. When numbers are absent we
  // render the step text instead of an empty "Generating of " label.
  const hasNumbers = activity.kind === 'active'
    && typeof activity.current === 'number'
    && typeof activity.total === 'number'
    && activity.total > 0;

  return (
    <>
      <div className={`act-pill ${open ? 'is-open' : ''}`} ref={ref}>
        <button className="act-pill-btn" onClick={pillBodyAction} type="button">
          <span className="act-spinner" aria-hidden>
            <span className="dot pulse" />
          </span>
          <span className="act-label">
            {activity.kind === 'planning' ? (
              <>Updating content plan · <b>{activity.step}</b></>
            ) : hasNumbers ? (
              <>Generating <b>{activity.current} of {activity.total}</b></>
            ) : (
              <><b>{activity.step || 'Working…'}</b></>
            )}
          </span>
          {pct != null && (
            <span className="act-prog" aria-hidden>
              <span className="act-prog-fill" style={{ width: pct + '%' }} />
            </span>
          )}
          <span
            role="button"
            tabIndex={0}
            aria-label="Toggle activity details"
            className="act-pill-toggle"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); } }}
            style={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            <I.chevDown size={12} />
          </span>
        </button>

        <div className="act-panel" role="dialog" aria-label="Activity">
          <div className="act-panel-h">
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                <span>{activity.kind === 'planning' ? 'Content plan update' : 'Batch generation'}</span>
              </div>
              <div className="act-title">{activity.project}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} type="button">
              <I.x size={13} />
            </button>
          </div>

          {activity.kind === 'active' && (
            <>
              <div className="act-meta">
                <div>
                  <div className="act-meta-l">Current step</div>
                  <div className="act-meta-v">
                    <span className="dot pulse" style={{ background: 'var(--accent)' }} /> {activity.step}
                  </div>
                </div>
                {hasNumbers && (
                  <div>
                    <div className="act-meta-l">Progress</div>
                    <div className="act-meta-v">
                      <b>{activity.current}</b> of {activity.total}
                    </div>
                  </div>
                )}
                {activity.eta && (
                  <div>
                    <div className="act-meta-l">ETA</div>
                    <div className="act-meta-v">{activity.eta}</div>
                  </div>
                )}
              </div>

              {hasNumbers && (
                <div className="act-progress">
                  <div className="act-progress-track">
                    <div className="act-progress-fill" style={{ width: pct + '%' }} />
                  </div>
                  <span className="mono act-pct">{pct}%</span>
                </div>
              )}

              {activity.items && (
                <div className="act-items">
                  {activity.items.slice(0, 6).map((it, i) => (
                    <div key={i} className={`act-item act-item-${it.status}`}>
                      <span className="act-item-ico">
                        {it.status === 'done' && <I.check size={11} />}
                        {it.status === 'doing' && <span className="dot pulse" style={{ background: 'currentColor' }} />}
                        {it.status === 'queued' && <span className="dot" style={{ background: 'currentColor', opacity: 0.5 }} />}
                      </span>
                      <span className="act-item-l">{it.label}</span>
                      <span className="act-item-r mono">{it.meta}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="act-foot">
                {activity.failed > 0 && (
                  <span className="mono" style={{ fontSize: 11, color: 'var(--danger-text)' }}>
                    {activity.failed} failed
                  </span>
                )}
                <span style={{ flex: 1 }} />
                {activity.returnTo ? (
                  <button
                    className="btn btn-soft btn-sm"
                    type="button"
                    onClick={() => { setOpen(false); navigate(activity.returnTo); }}
                  >
                    {activity.returnLabel || 'Open'} <I.arrowRight size={12} />
                  </button>
                ) : activity.batchId ? (
                  <button
                    className="btn btn-soft btn-sm"
                    type="button"
                    onClick={() => openBatch(activity.batchId)}
                  >
                    Open batch <I.arrowRight size={12} />
                  </button>
                ) : null}
              </div>
            </>
          )}

          {activity.kind === 'planning' && (
            <div style={{ padding: '18px 0 6px', color: 'var(--text-muted)', fontSize: 13 }}>
              {activity.step}
            </div>
          )}
        </div>
      </div>
      {toast && <ActivityToast data={toast} />}
    </>
  );
}
